import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { LogIn, UserPlus, AlertCircle } from 'lucide-react';

function formatError(detail) {
  if (!detail) return 'Something went wrong';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
}

function getSubmitLabel(loading, isRegister) {
  if (loading) return 'Please wait...';
  return isRegister ? 'Create Account' : 'Sign In';
}

export default function Login() {
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isRegister) {
        await register(email, password, name);
      } else {
        await login(email, password);
      }
      navigate('/admin');
    } catch (ex) {
      setError(formatError(ex.response?.data?.detail) || ex.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen bg-[#0B1D3A] pt-[68px] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6">
            <img src="/propel-logo.png" alt="Propel Sports" className="h-20 w-auto mx-auto" />
          </Link>
          <h1 className="text-2xl font-bold text-white mb-2">{isRegister ? 'Create Account' : 'Staff Login'}</h1>
          <p className="text-gray-400 text-sm">{isRegister ? 'Register for a new account' : 'Sign in to access the admin panel'}</p>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-2xl p-8 backdrop-blur-sm">
          {error && (
            <div data-testid="login-error" className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 mb-4">
              <AlertCircle size={16} className="text-red-400 shrink-0" />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} data-testid="login-form" className="space-y-4">
            {isRegister && (
              <div>
                <label className="block text-gray-400 text-xs mb-1.5">Name</label>
                <input type="text" required value={name} onChange={e => setName(e.target.value)}
                  data-testid="login-name" className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-[#F26522]/50" />
              </div>
            )}
            <div>
              <label className="block text-gray-400 text-xs mb-1.5">Email</label>
              <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                data-testid="login-email" className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-[#F26522]/50" />
            </div>
            <div>
              <label className="block text-gray-400 text-xs mb-1.5">Password</label>
              <input type="password" required value={password} onChange={e => setPassword(e.target.value)}
                data-testid="login-password" className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-[#F26522]/50" />
            </div>
            <button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full flex items-center justify-center gap-2 bg-[#F26522] hover:bg-[#D9551A] disabled:opacity-50 text-white px-6 py-3.5 rounded-lg font-semibold transition-all">
              {isRegister ? <UserPlus size={16} /> : <LogIn size={16} />}
              {getSubmitLabel(loading, isRegister)}
            </button>
          </form>

          <div className="mt-4 text-center">
            <button
              data-testid="toggle-auth-mode"
              onClick={() => { setIsRegister(!isRegister); setError(''); }}
              className="text-gray-400 text-sm hover:text-[#F26522] transition-colors"
            >
              {isRegister ? 'Already have an account? Sign in' : "Need an account? Register"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
